import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "@/components/ui/toast";
import { LOCAL_ONLY_NOTICE } from "@/lib/fixture-notice";
import { usePermission } from "@/hooks/use-permission";
import { MOCK_SVC_CATALOG, MOCK_SVC_CONNECTIVITY, SVC_PROBE_DEFAULTS } from "@/data/mock";

/* SVC-00/01/02 — Service Catalog / Connectivity Matrix / Probe Detail.
   Canonical source: prototype/pages/admin/services.html (no current Figma
   frame located). All 3 canonical screens share ONE page with 3 sub-tabs
   in the source, matching this task's own "do not assume all five
   screens require five separate routes" instruction and DD-005's own
   preferred interpretation (query-param tabs, matching the established
   `/admin/deployment/[id]?step=X` / `/admin/auth-config?tab=X`
   convention). `MOCK_SVC_CATALOG` is a distinct service list from the
   frozen Deployment flow's `DEP_SERVICES` — see its own doc-comment in
   data/mock.ts. The Probe Detail tab's default view IS a "running" state
   (RTT "—") even before any button is clicked, matching the canonical
   markup exactly; clicking "Run Now" or a catalog row's "Run probe →"
   both jump to this tab and replay the same completion animation the
   source itself defines (same source/target/protocol shown regardless of
   which service was clicked — the canonical source's own simplification,
   not one invented here). */

type Tab = "catalog" | "matrix" | "probe";

const TAB_LABELS: Record<Tab, string> = {
  catalog: "Service Catalog",
  matrix: "Connectivity Matrix",
  probe: "Probe Detail",
};

const RESULT_TONE: Record<string, "success" | "warning" | "danger"> = {
  Pass: "success",
  Partial: "warning",
  Fail: "danger",
};

export function ServiceDiagnostics({ initialTab }: { initialTab: Tab }) {
  const canRun = usePermission("admin:access");
  const [tab, setTab] = useState<Tab>(initialTab);
  const [probeStatus, setProbeStatus] = useState<"running" | "done">("running");
  const [probeWhich, setProbeWhich] = useState<string>("signer");

  function runProbe(which: string) {
    setProbeWhich(which);
    setTab("probe");
    setProbeStatus("running");
    setTimeout(() => setProbeStatus("done"), 1400);
  }

  function rerunMatrix() {
    toast({ variant: "info", title: "Re-running connectivity matrix… loopbacks complete", description: LOCAL_ONLY_NOTICE });
  }

  const rtt = probeWhich === "signer" ? "318 ms" : "58 ms";

  return (
    <div>
      <div className="mb-5">
        <h1 className="text-xl font-extrabold text-ink">Service Diagnostics</h1>
        <p className="mt-1 text-sm text-muted">Health, connectivity, and probe results for internal platform services.</p>
      </div>

      <div className="mb-5 flex border-b border-line">
        {(Object.keys(TAB_LABELS) as Tab[]).map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setTab(t)}
            className={`border-b-2 px-4 py-2.5 text-sm font-semibold transition-colors ${tab === t ? "border-primary text-primary" : "border-transparent text-muted hover:text-ink"}`}
          >
            {TAB_LABELS[t]}
          </button>
        ))}
      </div>

      {tab === "catalog" && (
        <div className="overflow-x-auto rounded-[10px] border border-line bg-surface">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-line bg-bg text-left text-xs font-semibold uppercase tracking-wide text-muted">
                <th className="px-4 py-2.5">Service</th>
                <th className="px-4 py-2.5">Status</th>
                <th className="px-4 py-2.5">Health</th>
                <th className="px-4 py-2.5">Latency</th>
                <th className="px-4 py-2.5">Version</th>
                <th className="px-4 py-2.5">Errors (24h)</th>
                <th className="px-4 py-2.5" />
              </tr>
            </thead>
            <tbody>
              {MOCK_SVC_CATALOG.map((s) => (
                <tr key={s.id} className="border-b border-line last:border-b-0">
                  <td className="px-4 py-3">
                    <div className="font-semibold text-ink">{s.name}</div>
                    <div className="font-mono text-xs text-muted">{s.hostname}</div>
                  </td>
                  <td className="px-4 py-3"><Badge variant={s.status === "Healthy" ? "success" : "warning"} size="sm">{s.status}</Badge></td>
                  <td className="px-4 py-3 text-ink">{s.uptime}</td>
                  <td className={`px-4 py-3 font-mono ${s.latencyWarn ? "text-warning" : "text-ink"}`}>{s.latencyMs}</td>
                  <td className="px-4 py-3 font-mono text-xs text-ink">{s.version}</td>
                  <td className={`px-4 py-3 font-semibold ${s.errors24h > 0 ? "text-warning" : "text-success"}`}>{s.errors24h}</td>
                  <td className="px-4 py-3 text-right">
                    <button
                      type="button"
                      disabled={!canRun}
                      className="text-xs font-semibold text-primary hover:underline disabled:cursor-not-allowed disabled:opacity-50"
                      onClick={() => canRun && runProbe(s.probeKey)}
                    >
                      Run probe →
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === "matrix" && (
        <div>
          <div className="overflow-x-auto rounded-[10px] border border-line bg-surface">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-line bg-bg text-left text-xs font-semibold uppercase tracking-wide text-muted">
                  <th className="px-4 py-2.5">Source → Target</th>
                  <th className="px-4 py-2.5">Result</th>
                  <th className="px-4 py-2.5">Latency</th>
                  <th className="px-4 py-2.5">Certificate</th>
                  <th className="px-4 py-2.5">Last Check</th>
                </tr>
              </thead>
              <tbody>
                {MOCK_SVC_CONNECTIVITY.map((c) => (
                  <tr key={c.pair} className="border-b border-line last:border-b-0">
                    <td className="px-4 py-3 font-semibold text-ink">{c.pair}</td>
                    <td className="px-4 py-3"><Badge variant={RESULT_TONE[c.result]} size="sm">{c.result}</Badge></td>
                    <td className={`px-4 py-3 font-mono ${c.result === "Fail" ? "text-danger" : c.result === "Partial" ? "text-warning" : "text-ink"}`}>{c.latency}</td>
                    <td className={`px-4 py-3 font-mono text-xs ${c.result === "Fail" ? "text-danger" : c.result === "Partial" ? "text-warning" : "text-muted"}`}>{c.certificate}</td>
                    <td className="px-4 py-3 font-mono text-xs text-muted">{c.lastCheck}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="mt-4 text-xs text-muted">
            Matrix checks run every 2 minutes across {MOCK_SVC_CONNECTIVITY.length} service pairs.{" "}
            <button type="button" disabled={!canRun} className="font-semibold text-primary hover:underline disabled:cursor-not-allowed disabled:opacity-50" onClick={() => canRun && rerunMatrix()}>Re-run all checks</button>
          </div>
        </div>
      )}

      {tab === "probe" && (
        <div>
          <div className="mb-2 flex items-center justify-between">
            <div>
              <div className="text-base font-bold text-ink">Probe Detail — {SVC_PROBE_DEFAULTS.label}</div>
              <div className="font-mono text-xs text-muted">{SVC_PROBE_DEFAULTS.probeId} · {SVC_PROBE_DEFAULTS.intervalLabel}</div>
            </div>
            <Button variant="secondary" size="sm" disabled={!canRun} onClick={() => canRun && runProbe(probeWhich)}>↻ Run Now</Button>
          </div>
          <div className="rounded-[10px] border border-line bg-surface p-5">
            <div className="mb-3 flex items-center gap-2.5">
              {probeStatus === "running" ? (
                <>
                  <span className="size-3 animate-spin rounded-full border-2 border-line-strong border-t-primary" aria-hidden />
                  <span className="text-sm text-muted">Probe running…</span>
                </>
              ) : (
                <span className="text-sm font-semibold text-success">✓ Completed — {probeWhich} reachable</span>
              )}
            </div>
            <dl className="space-y-2 text-sm">
              <div className="flex justify-between border-b border-line pb-2"><dt className="text-muted">Source</dt><dd className="font-mono text-ink">{SVC_PROBE_DEFAULTS.source}</dd></div>
              <div className="flex justify-between border-b border-line pb-2"><dt className="text-muted">Target</dt><dd className="font-mono text-ink">{SVC_PROBE_DEFAULTS.target}</dd></div>
              <div className="flex justify-between border-b border-line pb-2"><dt className="text-muted">Protocol</dt><dd className="font-mono text-ink">{SVC_PROBE_DEFAULTS.protocol}</dd></div>
              <div className="flex justify-between border-b border-line pb-2"><dt className="text-muted">Start</dt><dd className="font-mono text-ink">{SVC_PROBE_DEFAULTS.start}</dd></div>
              <div className="flex justify-between"><dt className="text-muted">RTT</dt><dd className="font-mono text-ink">{probeStatus === "done" ? rtt : "—"}</dd></div>
            </dl>
          </div>
        </div>
      )}
    </div>
  );
}
